import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type {
	Datastore,
	DatastoreItem,
	DatastorePath,
} from "../../../types/datastore";
import type { DialogProps } from "../../../types/dialogs";
import type { TableColumn, TableOptions } from "../../../types/table";
import datastorePassword from "../../services/datastore-password";
import helper from "../../services/helper";
import widgetService from "../../services/widget";
import ButtonDanger from "../button-danger";
import Table from "../table";
import DialogProgress from "./progress";
import DialogVerify from "./verify";

export interface DialogTrashBinProps extends DialogProps {
	datastore: Datastore;
}

type TrashRow = [id: string, name: string | undefined, path: string];
interface TrashEntry {
	path: DatastorePath;
	item: DatastoreItem;
	type: "folder" | "item";
}

function hasNodeId(node: Datastore): node is DatastoreItem {
	return typeof node.id === "string";
}

const DialogTrashBin = (props: DialogTrashBinProps) => {
	const { open, onClose, datastore } = props;
	const { t } = useTranslation();
	const [entries, setEntries] = useState<TrashRow[]>([]);
	const [entriesFull, setEntriesFull] = useState<Record<string, TrashEntry>>(
		{},
	);
	const [verifyDeletePermanentOpen, setVerifyDeletePermanentOpen] =
		useState(false);
	const [entryIndexBeingDeleted, setEntryIndexBeingDeleted] = useState<
		string | number
	>(0);
	const [verifyDeleteAllOpen, setVerifyDeleteAllOpen] = useState(false);
	const [deleteAllInProgress, setDeleteAllInProgress] = useState(false);
	const [deleteAllProgress, setDeleteAllProgress] = useState(0);

	let isSubscribed = true;
	React.useEffect(() => {
		fillRecyclingBinEntries(datastore);
		return () => {
			isSubscribed = false;
		};
	}, []);

	const fillRecyclingBinEntries = (datastore: Datastore) => {
		const newRecyclingBinEntries: TrashRow[] = [];
		const newRecyclingBinEntriesFull: Record<string, TrashEntry> = {};

		const fillRecyclingBinEntriesHelper = (
			folder: Datastore | undefined,
			path: DatastorePath,
		) => {
			let pathCopy: DatastorePath;

			if (typeof folder === "undefined") {
				return;
			}

			if (Object.hasOwn(folder, "deleted") && folder["deleted"]) {
				if (!hasNodeId(folder)) return;
				if (
					Object.hasOwn(folder, "share_rights") &&
					!folder.share_rights?.delete
				) {
					return;
				}
				newRecyclingBinEntries.push([
					folder.id,
					folder.name,
					datastorePassword.getFolderPath(path, datastore),
				]);
				newRecyclingBinEntriesFull[folder.id] = {
					path: path,
					item: folder,
					type: "folder",
				};
				return;
			}

			let i: number;
			for (
				i = 0;
				Object.hasOwn(folder, "folders") &&
				folder.folders &&
				i < folder.folders.length;
				i++
			) {
				pathCopy = path.slice();
				pathCopy.push(folder.folders[i].id);
				fillRecyclingBinEntriesHelper(folder.folders[i], pathCopy);
			}

			for (
				i = 0;
				Object.hasOwn(folder, "items") &&
				folder.items &&
				i < folder.items.length;
				i++
			) {
				if (
					!Object.hasOwn(folder.items[i], "deleted") ||
					!folder.items[i]["deleted"]
				) {
					continue;
				}

				pathCopy = path.slice();
				pathCopy.push(folder.items[i].id);

				if (
					!Object.hasOwn(folder.items[i], "share_rights") ||
					!folder.items[i].share_rights?.delete
				) {
					continue;
				}
				newRecyclingBinEntries.push([
					folder.items[i].id,
					folder.items[i].name,
					datastorePassword.getFolderPath(pathCopy, datastore),
				]);
				newRecyclingBinEntriesFull[folder.items[i].id] = {
					path: pathCopy,
					item: folder.items[i],
					type: "item",
				};
			}
		};

		fillRecyclingBinEntriesHelper(datastore, []);
		setEntries(newRecyclingBinEntries);
		setEntriesFull(newRecyclingBinEntriesFull);
	};

	const removeEntry = (index: string | number) => {
		const entriesClone = helper.duplicateObject(entries);
		const entriesFullClone = helper.duplicateObject(entriesFull);
		const arrayIndex = entriesClone.findIndex((entry) => {
			return entry[0] === index;
		});
		entriesClone.splice(arrayIndex, 1);
		setEntries(entriesClone);
		delete entriesFullClone[index];
		setEntriesFull(entriesFullClone);
	};

	const restore = (index: string) => {
		const entry = entriesFull[index];
		widgetService.reverseMarkItemAsDeleted(
			datastore,
			entry["item"],
			entry["path"],
			"password",
		);
		removeEntry(index);
	};

	const deletePermanent = (index: string) => {
		setEntryIndexBeingDeleted(index);
		setVerifyDeletePermanentOpen(true);
	};

	const deletePermanentConfirmed = () => {
		setVerifyDeletePermanentOpen(false);
		const entry = entriesFull[entryIndexBeingDeleted];
		widgetService.deleteItemPermanent(datastore, entry["path"], "password");
		removeEntry(entryIndexBeingDeleted);
	};

	const deleteAll = () => {
		if (entries.length === 0) {
			return;
		}

		setVerifyDeleteAllOpen(true);
	};

	const deleteAllConfirmed = async () => {
		setVerifyDeleteAllOpen(false);
		setDeleteAllInProgress(true);
		setDeleteAllProgress(0);

		const entryIds = entries.map((entry) => entry[0]);
		const entriesFullSnapshot = helper.duplicateObject(entriesFull);
		const totalEntries = entryIds.length;
		const deletedIds: string[] = [];

		for (let i = 0; i < entryIds.length; i++) {
			const entryId = entryIds[i];
			const entry = entriesFullSnapshot[entryId];

			if (entry) {
				await widgetService.deleteItemPermanent(
					datastore,
					entry["path"],
					"password",
				);
				deletedIds.push(entryId);

				// Update state immediately after each deletion
				setEntries((prevEntries) => {
					return prevEntries.filter((e) => !deletedIds.includes(e[0]));
				});

				setEntriesFull((prevEntriesFull) => {
					const newEntriesFull = helper.duplicateObject(prevEntriesFull);
					delete newEntriesFull[entryId];
					return newEntriesFull;
				});
			}

			const progress = Math.round(((i + 1) / totalEntries) * 100);
			setDeleteAllProgress(progress);

			// Small delay to allow UI to update
			await new Promise((resolve) => setTimeout(resolve, 100));
		}

		setDeleteAllInProgress(false);
		setDeleteAllProgress(0);
	};

	const columns: TableColumn<TrashRow>[] = [
		{ name: t("ID"), options: { display: false } },
		{
			name: t("TITLE"),
			options: {
				customBodyRender: (value, tableMeta) => {
					const folderPath = tableMeta.rowData[2];
					const displayedFolderPath =
						datastorePassword.abbreviateFolderPath(folderPath);
					return (
						<>
							{value}
							<Tooltip title={folderPath} placement="top">
								<FormHelperText
									style={{ overflow: "hidden", textOverflow: "ellipsis" }}
								>
									{displayedFolderPath}
								</FormHelperText>
							</Tooltip>
						</>
					);
				},
			},
		},
		{
			name: t("PATH"),
			options: {
				display: false,
				filter: false,
				sort: false,
				viewColumns: false,
			},
		},
		{
			name: t("RESTORE"),
			options: {
				filter: false,
				sort: false,
				empty: false,
				customBodyRender: (value, tableMeta, updateValue) => {
					return (
						<IconButton
							onClick={() => {
								restore(tableMeta.rowData[0]);
							}}
							size="large"
						>
							<RestoreFromTrashIcon />
						</IconButton>
					);
				},
			},
		},
		{
			name: t("DELETE"),
			options: {
				filter: false,
				sort: false,
				empty: false,
				customBodyRender: (value, tableMeta, updateValue) => {
					return (
						<IconButton
							onClick={() => {
								deletePermanent(tableMeta.rowData[0]);
							}}
							size="large"
						>
							<DeleteForeverIcon />
						</IconButton>
					);
				},
			},
		},
	];

	const options: TableOptions = {
		filterType: "checkbox",
	};

	return (
		<Dialog
			fullWidth
			maxWidth={"sm"}
			open={open}
			onClose={() => {
				onClose();
			}}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
		>
			<DialogTitle id="alert-dialog-title">{t("TRASH_BIN")}</DialogTitle>
			<DialogContent>
				<Table<TrashRow> data={entries} columns={columns} options={options} />
			</DialogContent>
			<DialogActions>
				<ButtonDanger
					onClick={deleteAll}
					disabled={entries.length === 0 || deleteAllInProgress}
				>
					{t("DELETE_ALL")}
				</ButtonDanger>
				<Button
					onClick={() => {
						onClose();
					}}
					disabled={deleteAllInProgress}
				>
					{t("CLOSE")}
				</Button>
			</DialogActions>
			{verifyDeletePermanentOpen && (
				<DialogVerify
					title={
						entriesFull[entryIndexBeingDeleted].type === "item"
							? "DELETE_ENTRY"
							: "DELETE_FOLDER"
					}
					description={
						entriesFull[entryIndexBeingDeleted].type === "item"
							? "DELETE_ENTRY_PERMANENT_WARNING"
							: "DELETE_FOLDER_PERMANENT_WARNING"
					}
					entries={[entriesFull[entryIndexBeingDeleted].item.name]}
					affectedEntriesText={
						entriesFull[entryIndexBeingDeleted].type === "item"
							? "AFFECTED_ENTRIES"
							: "AFFECTED_FOLDERS"
					}
					open={verifyDeletePermanentOpen}
					onClose={() => setVerifyDeletePermanentOpen(false)}
					onConfirm={deletePermanentConfirmed}
				/>
			)}
			{verifyDeleteAllOpen && (
				<DialogVerify
					title="DELETE_ALL"
					description="DELETE_ALL_PERMANENT_WARNING"
					entries={[]}
					affectedEntriesText=""
					open={verifyDeleteAllOpen}
					onClose={() => setVerifyDeleteAllOpen(false)}
					onConfirm={deleteAllConfirmed}
				/>
			)}
			{deleteAllInProgress && (
				<DialogProgress
					open={deleteAllInProgress}
					percentageComplete={deleteAllProgress}
				/>
			)}
		</Dialog>
	);
};

export default DialogTrashBin;
